import { Sparkles } from "lucide-react";
import { Button } from "~/components/ui/button";
import { AppIcon } from "~/components/ui/dynamic-icon";
import { TextOverflow } from "~/components/ui/text-overflow";
import { cn } from "~/lib/utils";
import type { AgentStarterPrompt } from "~/services/agents";
import { NEXTGEN_CHAT_EVENT, trackNextgenChatEvent } from "./analytics";

interface StarterPromptsProps {
  prompts: AgentStarterPrompt[];
  onPromptClick: (text: string) => void;
}

const StarterPromptButton = ({
  prompt,
  onPromptClick,
}: {
  prompt: AgentStarterPrompt;
  onPromptClick: (text: string) => void;
}) => (
  <Button
    variant="outline"
    size="sm"
    onClick={() => {
      trackNextgenChatEvent(
        NEXTGEN_CHAT_EVENT.PRESET_CLICKED,
        {},
        { prompt: prompt.text, prompt_id: prompt.id, position: prompt.position }
      );
      onPromptClick(prompt.text);
    }}
    className="min-w-0 flex-1 justify-start gap-2 rounded-full text-sm"
  >
    {prompt.icon && (
      <AppIcon name={prompt.icon} fallback={Sparkles} className="size-4 shrink-0 text-foreground" />
    )}
    <TextOverflow
      autoTooltip
      delayDuration={700}
      className={cn("text-foreground", prompt.title ? "font-semibold" : "font-normal")}
    >
      {prompt.title ?? prompt.text}
    </TextOverflow>
  </Button>
);

export const StarterPrompts = ({ prompts, onPromptClick }: StarterPromptsProps) => {
  const sorted = [...prompts].sort((a, b) => a.position - b.position);
  const splitIndex = sorted.length <= 3 ? sorted.length : Math.floor(sorted.length / 2);
  const topRow = sorted.slice(0, splitIndex);
  const bottomRow = sorted.slice(splitIndex);

  return (
    <div className="flex w-full max-w-2xl flex-col gap-2">
      <div className="flex gap-2">
        {topRow.map((prompt) => (
          <StarterPromptButton key={prompt.id} prompt={prompt} onPromptClick={onPromptClick} />
        ))}
      </div>
      {bottomRow.length > 0 && (
        <div className="flex gap-2">
          {bottomRow.map((prompt) => (
            <StarterPromptButton key={prompt.id} prompt={prompt} onPromptClick={onPromptClick} />
          ))}
        </div>
      )}
    </div>
  );
};
